/**
 * PC Builder Main Module
 *
 * Handles component selection, compatibility checking, and build management
 */

class PCBuilder {
  constructor(container) {
    this.container = container;
    this.apiUrl = container.dataset.apiUrl || 'https://easybuild.easypc.com.ph/backend-deploy/backend/api';
    this.state = {
      components: {},
      totalPrice: 0,
      totalPower: 0,
      warnings: [],
      buildName: ''
    };

    this.modal = container.querySelector('[data-component-modal]');
    this.currentType = null;

    this.init();
  }

  init() {
    this.attachEventListeners();
    this.loadFromStorage();
    this.checkForSharedBuild();
  }

  attachEventListeners() {
    // Component selection buttons
    this.container.querySelectorAll('[data-action="select-component"]').forEach(btn => {
      btn.addEventListener('click', () => this.openModal(btn.dataset.type));
    });

    // Modal close
    this.modal.querySelectorAll('[data-modal-close]').forEach(btn => {
      btn.addEventListener('click', () => this.closeModal());
    });

    // Build actions
    this.container.querySelector('[data-action="save-build"]')?.addEventListener('click', () => this.saveBuild());
    this.container.querySelector('[data-action="load-build"]')?.addEventListener('click', () => this.loadBuild());
    this.container.querySelector('[data-action="share-build"]')?.addEventListener('click', () => this.shareBuild());
    this.container.querySelector('[data-action="clear-build"]')?.addEventListener('click', () => this.clearBuild());
    this.container.querySelector('[data-action="add-to-cart"]')?.addEventListener('click', () => this.addToCart());
    this.container.querySelector('[data-action="export-reddit"]')?.addEventListener('click', () => this.exportReddit());
    this.container.querySelector('[data-action="get-recommendations"]')?.addEventListener('click', () => this.showRecommendations());

    // Build name input
    const nameInput = this.container.querySelector('[data-build-name]');
    if (nameInput) {
      nameInput.addEventListener('input', (e) => {
        this.state.buildName = e.target.value;
        this.saveToStorage();
      });
    }

    // Component search
    const searchInput = this.modal.querySelector('[data-component-search]');
    if (searchInput) {
      searchInput.addEventListener('input', (e) => this.searchComponents(e.target.value));
    }

    // Show out of stock filter
    const stockFilter = this.modal.querySelector('[data-show-out-of-stock]');
    if (stockFilter) {
      stockFilter.addEventListener('change', (e) => this.filterByStock(e.target.checked));
    }
  }

  async openModal(type) {
    this.currentType = type;
    this.allComponents = [];
    this.filteredComponents = [];
    this.currentPage = 1;
    this.hasMore = false;
    this.isLoadingMore = false;

    const title = this.modal.querySelector('[data-modal-title]');
    const content = this.modal.querySelector('[data-modal-content]');
    const loading = this.modal.querySelector('[data-modal-loading]');
    const searchInput = this.modal.querySelector('[data-component-search]');

    // Clear and setup search input
    if (searchInput) {
      console.log('Search input found, attaching listener');
      searchInput.value = '';
      // Remove old listener and add new one to avoid duplicates
      if (this.searchHandler) {
        searchInput.removeEventListener('input', this.searchHandler);
      }
      this.searchHandler = (e) => {
        console.log('Input event fired');
        this.searchComponents(e.target.value);
      };
      searchInput.addEventListener('input', this.searchHandler);
    } else {
      console.error('Search input NOT found!');
    }

    title.textContent = `Select ${this.formatComponentType(type)}`;
    this.modal.classList.add('is-open');

    loading.style.display = 'flex';
    content.innerHTML = '';

    // Reset stock filter checkbox
    const stockFilter = this.modal.querySelector('[data-show-out-of-stock]');
    if (stockFilter) {
      stockFilter.checked = true; // Default: show all products
    }

    try {
      // PHASE 1: Load in-stock products first (fast results)
      console.log('PHASE 1: Fetching in-stock products first...');
      const inStockComponents = await this.fetchInStockFirst(type);

      if (inStockComponents.length > 0) {
        this.allComponents = inStockComponents;
        this.filteredComponents = inStockComponents;
        loading.style.display = 'none';
        this.renderComponents(inStockComponents);
        this.updateCompatibilityHints(type);

        // Show loading indicator for remaining products
        const loadingMore = document.createElement('div');
        loadingMore.className = 'loading-more-indicator';
        loadingMore.innerHTML = '<div class="spinner-small"></div><span>Loading more products...</span>';
        loadingMore.setAttribute('data-loading-more', '');
        content.appendChild(loadingMore);
      } else {
        // If no in-stock products, show loading state
        loading.style.display = 'flex';
      }

      // PHASE 2: Load remaining products in background (slower)
      console.log('PHASE 2: Loading remaining products in background...');
      setTimeout(async () => {
        try {
          const allComponents = await this.fetchComponents(type);
          this.allComponents = allComponents;
          this.filteredComponents = allComponents;

          // Remove "loading more" indicator
          const loadingMoreEl = content.querySelector('[data-loading-more]');
          if (loadingMoreEl) loadingMoreEl.remove();

          loading.style.display = 'none';
          this.renderComponents(allComponents);
          console.log('PHASE 2 complete: All products loaded');
        } catch (error) {
          console.error('Error loading remaining products:', error);
          const loadingMoreEl = content.querySelector('[data-loading-more]');
          if (loadingMoreEl) {
            loadingMoreEl.innerHTML = '⚠️ Some products failed to load';
          }
        }
      }, 100); // Small delay to let UI render first

    } catch (error) {
      console.error('Error fetching components:', error);
      content.innerHTML = '<p class="error">Failed to load components. Please try again.</p>';
      loading.style.display = 'none';
    }
  }

  closeModal() {
    this.modal.classList.remove('is-open');
    this.currentType = null;
  }

  async fetchComponents(type, page = 1) {
    const response = await fetch(`${this.apiUrl}/components/search-fixed.php?type=${type}&limit=50&page=${page}`);
    if (!response.ok) throw new Error('Failed to fetch components');
    const data = await response.json();
    console.log('API Response:', data);

    // Store pagination info
    if (data.pagination) {
      this.hasMore = data.pagination.has_more;
      this.currentPage = data.pagination.page;
      this.totalProducts = data.pagination.total;
    }

    return data.data || [];
  }

  async fetchInStockFirst(type) {
    // Fetch first page of components (backend already sorts in-stock first)
    const allComponents = await this.fetchComponents(type, 1);

    // Filter to only in-stock products
    const inStockComponents = allComponents.filter(c => c.available === true);

    console.log(`PHASE 1: Found ${inStockComponents.length} in-stock products out of ${allComponents.length} total`);

    return inStockComponents;
  }

  renderComponents(components, append = false) {
    const content = this.modal.querySelector('[data-modal-content]');

    if (components.length === 0 && !append) {
      content.innerHTML = '<p>No components found.</p>';
      return;
    }

    // Annotate components with compatibility
    const annotated = components.map(component => ({
      ...component,
      compatibility: this.checkComponentCompatibility(this.currentType, component)
    }));

    // Sort: 1) In stock first, 2) Compatible first, 3) By price
    annotated.sort((a, b) => {
      // Priority 1: In stock products first
      const aInStock = a.available ? 1 : 0;
      const bInStock = b.available ? 1 : 0;
      if (aInStock !== bInStock) return bInStock - aInStock;

      // Priority 2: Compatible products first
      const aCompat = a.compatibility.compatible ? 1 : 0;
      const bCompat = b.compatibility.compatible ? 1 : 0;
      if (aCompat !== bCompat) return bCompat - aCompat;

      // Priority 3: Lower price first
      return (a.price || 999999) - (b.price || 999999);
    });

    const componentsHTML = annotated.map(component => this.renderComponentCard(component)).join('');

    if (append) {
      // Append new components
      const loadMoreBtn = content.querySelector('[data-load-more]');
      if (loadMoreBtn) {
        loadMoreBtn.insertAdjacentHTML('beforebegin', componentsHTML);
      } else {
        content.insertAdjacentHTML('beforeend', componentsHTML);
      }
    } else {
      // Replace all content
      content.innerHTML = componentsHTML;
    }

    // Add or update Load More button
    this.updateLoadMoreButton();

    // Attach select handlers to new components
    content.querySelectorAll('[data-action="select-component"]').forEach(btn => {
      btn.addEventListener('click', () => {
        const productId = btn.dataset.productId;
        const component = annotated.find(c => c.id == productId);
        if (component) {
          this.selectComponent(this.currentType, component);
          this.closeModal();
        }
      });
    });
  }

  updateLoadMoreButton() {
    const content = this.modal.querySelector('[data-modal-content]');
    let loadMoreBtn = content.querySelector('[data-load-more]');

    // Remove existing button
    if (loadMoreBtn) {
      loadMoreBtn.remove();
    }

    // Add new button if there are more products
    if (this.hasMore) {
      const showingCount = this.allComponents.length;
      const totalCount = this.totalProducts || showingCount;

      loadMoreBtn = document.createElement('div');
      loadMoreBtn.setAttribute('data-load-more', '');
      loadMoreBtn.style.cssText = 'text-align: center; padding: 1.5rem; border-top: 1px solid #e5e7eb;';
      loadMoreBtn.innerHTML = `
        <p style="margin-bottom: 0.5rem; color: #6b7280; font-size: 0.875rem;">
          Showing ${showingCount} of ${totalCount} products
        </p>
        <button type="button" class="btn btn--secondary" data-action="load-more-components">
          Load More (50)
        </button>
      `;

      content.appendChild(loadMoreBtn);

      // Attach load more handler
      const btn = loadMoreBtn.querySelector('[data-action="load-more-components"]');
      btn.addEventListener('click', () => this.loadMoreComponents());
    }
  }

  async loadMoreComponents() {
    if (this.isLoadingMore || !this.hasMore) return;

    this.isLoadingMore = true;
    const btn = this.modal.querySelector('[data-action="load-more-components"]');
    const originalText = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Loading...';

    try {
      const nextPage = this.currentPage + 1;
      const newComponents = await this.fetchComponents(this.currentType, nextPage);

      // Add to allComponents
      this.allComponents.push(...newComponents);
      this.filteredComponents = this.allComponents;

      // Render new components (append mode)
      this.renderComponents(newComponents, true);
    } catch (error) {
      console.error('Error loading more components:', error);
      alert('Failed to load more components. Please try again.');
    } finally {
      this.isLoadingMore = false;
      if (btn) {
        btn.disabled = false;
        btn.textContent = originalText;
      }
    }
  }

  renderComponentCard(component) {
    const image = component.image || component.product_data?.images?.[0]?.src || '';
    const specs = this.getComponentSpecs(component);
    const compat = component.compatibility || { compatible: true, warnings: [] };
    const compatClass = compat.compatible ? 'compatible' : 'incompatible';
    const stockClass = component.available ? '' : 'component-card--out-of-stock';

    return `
      <div class="component-card component-card--${compatClass} ${stockClass}" data-product-id="${component.id}">
        ${!compat.compatible ? '<div class="component-card__compat-badge">⚠️ Incompatible</div>' : ''}
        ${component.available ? '<div class="component-card__stock-badge component-card__stock-badge--in-stock">✓ In Stock</div>' : '<div class="component-card__stock-badge component-card__stock-badge--out">Out of Stock</div>'}
        <div class="component-card__image">
          ${image ? `<img src="${image}" alt="${component.title}" loading="lazy">` :
            '<div class="component-card__no-image"><svg class="icon" width="48" height="48"><use xlink:href="#icon-image"></use></svg></div>'}
        </div>
        <div class="component-card__content">
          <h4 class="component-card__title">${component.title}</h4>
          <div class="component-card__specs">${specs}</div>
          ${compat.warnings.length > 0 ? `
            <div class="component-card__warnings">
              ${compat.warnings.slice(0, 2).map(w => `<small class="warning-text">⚠️ ${w}</small>`).join('')}
            </div>
          ` : ''}
          <div class="component-card__footer">
            <div class="component-card__price">${component.price ? '₱' + parseFloat(component.price).toLocaleString('en-PH', {minimumFractionDigits: 2, maximumFractionDigits: 2}) : 'Price N/A'}</div>
          </div>
        </div>
        <div class="component-card__actions">
          <button type="button" class="btn btn--small btn--primary btn--full"
                  data-action="select-component" data-product-id="${component.id}"
                  ${!component.available ? 'disabled' : ''}>
            ${component.available ? 'Select' : 'Unavailable'}
          </button>
        </div>
      </div>
    `;
  }

  getComponentSpecs(component) {
    // Use pcbuilder_v2 namespace
    const meta = component.metafields?.pcbuilder_v2 || {};
    const type = component.component_type;
    const specs = [];

    if (type === 'cpu') {
      if (meta['cpu.socket']) specs.push(meta['cpu.socket']);
      if (meta['cpu.memory_types']) specs.push(meta['cpu.memory_types']);
      if (meta['cpu.tdp']) specs.push(`${meta['cpu.tdp']}W TDP`);
    } else if (type === 'motherboard') {
      if (meta['mobo.socket']) specs.push(meta['mobo.socket']);
      if (meta['mobo.form_factor']) specs.push(meta['mobo.form_factor']);
      if (meta['mobo.chipset']) specs.push(meta['mobo.chipset']);
      if (meta['mobo.memory_type']) specs.push(meta['mobo.memory_type']);
    } else if (type === 'gpu') {
      if (meta['gpu.length_mm']) specs.push(`${meta['gpu.length_mm']}mm`);
      if (meta['gpu.tdp']) specs.push(`${meta['gpu.tdp']}W`);
      if (meta['gpu.power_connector']) specs.push(meta['gpu.power_connector']);
      if (meta['gpu.pcie_gen']) specs.push(`PCIe ${meta['gpu.pcie_gen']}`);
    } else if (type === 'ram') {
      if (meta['ram.memory_type']) specs.push(meta['ram.memory_type']);
      if (meta['ram.speed_mhz']) specs.push(`${meta['ram.speed_mhz']}MHz`);
      if (meta['ram.capacity_gb']) specs.push(`${meta['ram.capacity_gb']}GB`);
      if (meta['ram.modules']) specs.push(meta['ram.modules']);
    } else if (type === 'psu') {
      if (meta['psu.wattage']) specs.push(`${meta['psu.wattage']}W`);
      if (meta['psu.efficiency']) specs.push(meta['psu.efficiency']);
      if (meta['psu.modular']) specs.push(meta['psu.modular']);
    }

    return specs.map(s => `<span class="spec-badge">${s}</span>`).join('');
  }

  selectComponent(type, component) {
    this.state.components[type] = component;
    this.updateSlot(type, component);
    this.updateSummary();
    this.checkCompatibility();
    this.saveToStorage();
  }

  updateSlot(type, component) {
    const slot = this.container.querySelector(`[data-slot-content="${type}"]`);
    if (!slot) return;

    const image = component.product_data?.images?.[0]?.src || '';
    slot.innerHTML = `
      <div class="build-item" data-component-type="${type}">
        <div class="build-item__image">
          ${image ? `<img src="${image}" alt="${component.title}">` :
            '<div class="build-item__no-image"><svg class="icon" width="24" height="24"><use xlink:href="#icon-image"></use></svg></div>'}
        </div>
        <div class="build-item__content">
          <div class="build-item__type">${this.formatComponentType(type)}</div>
          <div class="build-item__title">${component.title}</div>
          <div class="build-item__price">${component.price ? '₱' + parseFloat(component.price).toLocaleString('en-PH', {minimumFractionDigits: 2, maximumFractionDigits: 2}) : 'Price N/A'}</div>
        </div>
        <div class="build-item__actions">
          <button type="button" class="build-item__remove" data-action="remove-component" data-type="${type}">
            <svg class="icon" width="16" height="16"><use xlink:href="#icon-x"></use></svg>
          </button>
        </div>
      </div>
    `;

    slot.querySelector('[data-action="remove-component"]').addEventListener('click', () => this.removeComponent(type));
  }

  removeComponent(type) {
    delete this.state.components[type];
    const slot = this.container.querySelector(`[data-slot-content="${type}"]`);
    if (slot) {
      slot.innerHTML = `<div class="component-slot__empty"><span>No ${this.formatComponentType(type)} selected</span></div>`;
    }
    this.updateSummary();
    this.checkCompatibility();
    this.saveToStorage();
  }

  updateSummary() {
    const components = Object.values(this.state.components);
    this.state.totalPrice = components.reduce((sum, c) => sum + (parseFloat(c.price) || 0), 0);
    this.state.totalPower = components.reduce((sum, c) => {
      const meta = c.metafields;
      const type = c.component_type;
      if (type === 'cpu' && meta?.cpu?.power_watts) return sum + parseInt(meta.cpu.power_watts);
      if (type === 'gpu' && meta?.gpu?.power_watts) return sum + parseInt(meta.gpu.power_watts);
      if (type === 'motherboard') return sum + 60;
      if (type === 'ram' && meta?.ram) return sum + (meta.ram.memory_type === 'DDR5' ? 8 : 5);
      if (type === 'storage') return sum + 8;
      return sum;
    }, 0);

    this.container.querySelector('[data-total-price] .build-summary__price-value').textContent = '₱' + this.state.totalPrice.toLocaleString('en-PH', {minimumFractionDigits: 2, maximumFractionDigits: 2});
    this.container.querySelector('[data-power-used]').textContent = this.state.totalPower + 'W';

    const psu = this.state.components.psu;
    if (psu?.metafields?.psu?.wattage) {
      const psuWatts = parseInt(psu.metafields.psu.wattage);
      this.container.querySelector('[data-power-available]').textContent = psuWatts + 'W';
      const percent = Math.min((this.state.totalPower / psuWatts) * 100, 100);
      this.container.querySelector('.build-summary__power-used').style.width = percent + '%';
    }
  }

  async checkCompatibility() {
    const components = this.state.components;
    if (Object.keys(components).length < 2) {
      this.state.warnings = [];
      this.updateWarnings();
      return;
    }

    try {
      // Send product IDs, not full component objects
      const payload = {};
      Object.entries(components).forEach(([type, component]) => {
        payload[type] = component.id;
      });

      const response = await fetch(`${this.apiUrl}/compatibility/check.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      console.log('Compatibility check response:', data);

      if (data.success && data.data) {
        this.state.warnings = [...(data.data.errors || []), ...(data.data.warnings || [])];
        this.state.compatible = data.data.compatible;

        // Store power consumption if available
        if (data.data.power_consumption) {
          this.state.power_consumption = data.data.power_consumption;
        }
      }

      this.updateWarnings();
      this.updateCompatibilityStatus();
    } catch (error) {
      console.error('Compatibility check failed:', error);
    }
  }

  updateWarnings() {
    const container = this.container.querySelector('[data-warnings-container]');
    if (this.state.warnings.length === 0) {
      container.innerHTML = '<p class="build-summary__no-warnings">No issues detected</p>';
      return;
    }

    container.innerHTML = this.state.warnings.map(w => `
      <div class="compatibility-badge compatibility-badge--${w.priority}">
        <div class="compatibility-badge__icon">
          <svg class="icon" width="20" height="20">
            <use xlink:href="#icon-alert-${w.priority === 'critical' ? 'circle' : 'triangle'}"></use>
          </svg>
        </div>
        <div class="compatibility-badge__content">
          <div class="compatibility-badge__message">${w.message}</div>
        </div>
        <div class="compatibility-badge__priority">
          <span class="badge badge--${w.priority}">${w.priority}</span>
        </div>
      </div>
    `).join('');
  }

  updateCompatibilityStatus() {
    const status = this.container.querySelector('[data-status-container] .compatibility-status');
    const hasErrors = this.state.warnings.some(w => w.priority === 'critical');
    const hasWarnings = this.state.warnings.some(w => w.priority === 'high' || w.priority === 'medium');

    status.className = 'compatibility-status';
    if (hasErrors) {
      status.classList.add('compatibility-status--error');
      status.querySelector('.compatibility-status__text').textContent = 'Compatibility issues found';
    } else if (hasWarnings) {
      status.classList.add('compatibility-status--warning');
      status.querySelector('.compatibility-status__text').textContent = 'Minor compatibility warnings';
    } else if (Object.keys(this.state.components).length > 0) {
      status.classList.add('compatibility-status--valid');
      status.querySelector('.compatibility-status__text').textContent = 'All components compatible';
    }
  }

  saveToStorage() {
    localStorage.setItem('pcBuilderState', JSON.stringify(this.state));
  }

  loadFromStorage() {
    const saved = localStorage.getItem('pcBuilderState');
    if (saved) {
      this.state = JSON.parse(saved);
      Object.entries(this.state.components).forEach(([type, component]) => {
        this.updateSlot(type, component);
      });
      const nameInput = this.container.querySelector('[data-build-name]');
      if (nameInput) nameInput.value = this.state.buildName;
      this.updateSummary();
      this.checkCompatibility();
    }
  }

  clearBuild() {
    if (!confirm('Clear all components from this build?')) return;
    this.state = { components: {}, totalPrice: 0, totalPower: 0, warnings: [], buildName: '' };
    Object.keys(this.state.components).forEach(type => this.removeComponent(type));
    localStorage.removeItem('pcBuilderState');
  }

  async saveBuild() {
    const components = Object.values(this.state.components);

    if (components.length === 0) {
      alert('No components selected. Please add components to your build first.');
      return;
    }

    const buildName = this.state.buildName.trim() || 'Untitled Build';

    const btn = this.container.querySelector('[data-action="save-build"]');
    const originalText = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Saving...';

    try {
      // Prepare build data
      const buildData = {
        name: buildName,
        components: {}
      };

      Object.entries(this.state.components).forEach(([type, component]) => {
        buildData.components[type] = {
          shopify_product_id: component.id,
          title: component.title,
          price: component.price,
          metafields: component.metafields
        };
      });

      // Try to save to backend API
      const response = await fetch(`${this.apiUrl}/builds/save.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildData)
      });

      if (response.ok) {
        const result = await response.json();
        const buildId = result.data?.build?.id;

        // Store build ID for future updates
        localStorage.setItem('currentBuildId', buildId);

        alert(`Build "${buildName}" saved successfully! Build ID: ${buildId}`);
      } else {
        // Fallback: Save to localStorage
        throw new Error('API save failed');
      }

    } catch (error) {
      console.warn('Backend save failed, using localStorage:', error);

      // Fallback: Save to localStorage
      const savedBuilds = JSON.parse(localStorage.getItem('savedBuilds') || '[]');
      const buildId = Date.now().toString();

      const build = {
        id: buildId,
        name: buildName,
        components: this.state.components,
        created: new Date().toISOString(),
        totalPrice: this.state.totalPrice,
        totalPower: this.state.totalPower
      };

      savedBuilds.push(build);
      localStorage.setItem('savedBuilds', JSON.stringify(savedBuilds));
      localStorage.setItem('currentBuildId', buildId);

      alert(`Build "${buildName}" saved locally! (Build ID: ${buildId})`);
    } finally {
      btn.disabled = false;
      btn.textContent = originalText;
    }
  }

  async loadBuild() {
    // Check for saved builds in localStorage
    const savedBuilds = JSON.parse(localStorage.getItem('savedBuilds') || '[]');

    if (savedBuilds.length === 0) {
      alert('No saved builds found. Save a build first!');
      return;
    }

    // Create selection modal
    const modal = document.createElement('div');
    modal.className = 'modal is-open';
    modal.innerHTML = `
      <div class="modal__overlay"></div>
      <div class="modal__container">
        <div class="modal__header">
          <h3 class="modal__title">Load Build</h3>
          <button type="button" class="modal__close" data-close-load-modal>
            <svg class="icon" width="24" height="24"><use xlink:href="#icon-x"></use></svg>
          </button>
        </div>
        <div class="modal__body">
          <div class="saved-builds-list">
            ${savedBuilds.map(build => `
              <div class="saved-build-item" data-build-id="${build.id}">
                <div class="saved-build-item__header">
                  <h4 class="saved-build-item__name">${build.name}</h4>
                  <button type="button" class="btn btn--small btn--secondary" data-action="delete-build" data-build-id="${build.id}">
                    <svg class="icon" width="16" height="16"><use xlink:href="#icon-trash"></use></svg>
                  </button>
                </div>
                <div class="saved-build-item__info">
                  <span>Components: ${Object.keys(build.components).length}</span>
                  <span>Total: ₱${(build.totalPrice || 0).toLocaleString('en-PH', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                  <span>${new Date(build.created).toLocaleDateString()}</span>
                </div>
                <button type="button" class="btn btn--primary btn--full btn--small" data-action="load-this-build" data-build-id="${build.id}">
                  Load Build
                </button>
              </div>
            `).join('')}
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    // Add styles for saved builds list
    if (!document.getElementById('load-build-styles')) {
      const style = document.createElement('style');
      style.id = 'load-build-styles';
      style.textContent = `
        .saved-builds-list { display: flex; flex-direction: column; gap: 1rem; }
        .saved-build-item { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 0.5rem; padding: 1rem; }
        .saved-build-item__header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.5rem; }
        .saved-build-item__name { margin: 0; font-size: 1.125rem; font-weight: 600; }
        .saved-build-item__info { display: flex; gap: 1rem; font-size: 0.875rem; color: #6b7280; margin-bottom: 1rem; }
      `;
      document.head.appendChild(style);
    }

    // Close modal handler
    modal.querySelector('[data-close-load-modal]').addEventListener('click', () => modal.remove());
    modal.querySelector('.modal__overlay').addEventListener('click', () => modal.remove());

    // Load build handlers
    modal.querySelectorAll('[data-action="load-this-build"]').forEach(btn => {
      btn.addEventListener('click', () => {
        const buildId = btn.dataset.buildId;
        const build = savedBuilds.find(b => b.id === buildId);
        if (build) {
          this.loadBuildData(build);
          modal.remove();
        }
      });
    });

    // Delete build handlers
    modal.querySelectorAll('[data-action="delete-build"]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const buildId = btn.dataset.buildId;
        if (confirm('Delete this build?')) {
          const filtered = savedBuilds.filter(b => b.id !== buildId);
          localStorage.setItem('savedBuilds', JSON.stringify(filtered));
          modal.remove();
          alert('Build deleted!');
        }
      });
    });
  }

  loadBuildData(build) {
    // Clear current build
    Object.keys(this.state.components).forEach(type => {
      const slot = this.container.querySelector(`[data-slot-content="${type}"]`);
      if (slot) {
        slot.innerHTML = `<div class="component-slot__empty"><span>No ${this.formatComponentType(type)} selected</span></div>`;
      }
    });

    // Load components
    this.state.components = build.components;
    this.state.buildName = build.name;

    // Update UI
    Object.entries(build.components).forEach(([type, component]) => {
      this.updateSlot(type, component);
    });

    const nameInput = this.container.querySelector('[data-build-name]');
    if (nameInput) nameInput.value = build.name;

    this.updateSummary();
    this.checkCompatibility();
    this.saveToStorage();

    alert(`Build "${build.name}" loaded successfully!`);
  }

  async shareBuild() {
    const components = Object.values(this.state.components);

    if (components.length === 0) {
      alert('No components selected. Please add components to your build first.');
      return;
    }

    const buildName = this.state.buildName.trim() || 'Untitled Build';

    const btn = this.container.querySelector('[data-action="share-build"]');
    const originalText = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Generating Link...';

    try {
      // Prepare build data
      const buildData = {
        name: buildName,
        components: {}
      };

      Object.entries(this.state.components).forEach(([type, component]) => {
        buildData.components[type] = {
          shopify_product_id: component.id,
          title: component.title,
          price: component.price,
          metafields: component.metafields
        };
      });

      // Generate anonymous share token via API
      const response = await fetch(`${this.apiUrl}/builds/share.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildData)
      });

      if (!response.ok) {
        throw new Error('Failed to generate share link');
      }

      const result = await response.json();
      const shareToken = result.data?.token;
      const shareUrl = `${window.location.origin}/pages/pc-builder?share=${shareToken}`;

      // Copy to clipboard
      await navigator.clipboard.writeText(shareUrl);

      // Show modal with share link
      this.showShareModal(shareUrl, shareToken);

    } catch (error) {
      console.error('Share build failed:', error);
      alert('Failed to generate share link: ' + error.message);
    } finally {
      btn.disabled = false;
      btn.textContent = originalText;
    }
  }

  showShareModal(shareUrl, token) {
    const modal = document.createElement('div');
    modal.className = 'modal is-open';
    modal.innerHTML = `
      <div class="modal__overlay"></div>
      <div class="modal__container">
        <div class="modal__header">
          <h3 class="modal__title">Share Build</h3>
          <button type="button" class="modal__close" data-close-share-modal>
            <svg class="icon" width="24" height="24"><use xlink:href="#icon-x"></use></svg>
          </button>
        </div>
        <div class="modal__body">
          <p style="margin-bottom: 1rem;">Your build has been saved! Share this link with anyone:</p>
          <div style="display: flex; gap: 0.5rem; margin-bottom: 1rem;">
            <input type="text" value="${shareUrl}" readonly
                   style="flex: 1; padding: 0.75rem; border: 2px solid #e5e7eb; border-radius: 0.375rem; font-size: 0.875rem;">
            <button type="button" class="btn btn--primary" data-action="copy-share-url" data-url="${shareUrl}">
              Copy
            </button>
          </div>
          <p style="font-size: 0.875rem; color: #6b7280;">
            <strong>Share Token:</strong> ${token}<br>
            This link will allow anyone to view and load your build.
          </p>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    // Close modal handler
    modal.querySelector('[data-close-share-modal]').addEventListener('click', () => modal.remove());
    modal.querySelector('.modal__overlay').addEventListener('click', () => modal.remove());

    // Copy button handler
    modal.querySelector('[data-action="copy-share-url"]').addEventListener('click', async (e) => {
      const url = e.target.dataset.url;
      try {
        await navigator.clipboard.writeText(url);
        e.target.textContent = 'Copied!';
        setTimeout(() => {
          if (e.target) e.target.textContent = 'Copy';
        }, 2000);
      } catch (error) {
        alert('Failed to copy to clipboard');
      }
    });

    alert('Share link copied to clipboard!');
  }

  // Check for shared build on page load
  checkForSharedBuild() {
    const params = new URLSearchParams(window.location.search);
    const shareToken = params.get('share');

    if (shareToken) {
      this.loadSharedBuild(shareToken);
    }
  }

  async loadSharedBuild(token) {
    try {
      const response = await fetch(`${this.apiUrl}/builds/load.php?token=${token}`);

      if (!response.ok) {
        throw new Error('Failed to load shared build');
      }

      const result = await response.json();
      const build = result.data?.build;

      if (!build) {
        throw new Error('Build not found');
      }

      // Load the build data
      this.state.buildName = build.name + ' (Shared)';
      this.state.components = {};

      // Reconstruct components from build data
      Object.entries(build.components).forEach(([type, componentData]) => {
        this.state.components[type] = {
          id: componentData.shopify_product_id,
          title: componentData.title,
          price: componentData.price,
          metafields: componentData.metafields,
          component_type: type,
          available: true
        };
      });

      // Update UI
      Object.entries(this.state.components).forEach(([type, component]) => {
        this.updateSlot(type, component);
      });

      const nameInput = this.container.querySelector('[data-build-name]');
      if (nameInput) nameInput.value = this.state.buildName;

      this.updateSummary();
      this.checkCompatibility();
      this.saveToStorage();

      alert(`Loaded shared build: "${build.name}"`);

    } catch (error) {
      console.error('Failed to load shared build:', error);
      alert('Failed to load shared build. The link may be invalid or expired.');
    }
  }

  async addToCart() {
    const components = Object.values(this.state.components);

    if (components.length === 0) {
      alert('No components selected. Please add components to your build first.');
      return;
    }

    // Check for critical compatibility issues
    const hasErrors = this.state.warnings.some(w => w.priority === 'critical');
    if (hasErrors) {
      if (!confirm('This build has compatibility errors. Add to cart anyway?')) {
        return;
      }
    }

    const btn = this.container.querySelector('[data-action="add-to-cart"]');
    const originalText = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding to Cart...';

    try {
      // Prepare items for Shopify Cart API
      const items = components
        .filter(c => c.product_data?.variants?.[0]?.id)
        .map(c => ({
          id: c.product_data.variants[0].id,
          quantity: 1
        }));

      if (items.length === 0) {
        throw new Error('No valid products to add to cart');
      }

      // Add items to Shopify cart
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.description || 'Failed to add to cart');
      }

      // Success - redirect to cart or show notification
      const result = await response.json();
      alert(`Successfully added ${result.items?.length || items.length} components to cart!`);

      // Optionally redirect to cart
      if (confirm('View cart now?')) {
        window.location.href = '/cart';
      }

    } catch (error) {
      console.error('Add to cart failed:', error);
      alert('Failed to add components to cart: ' + error.message);
    } finally {
      btn.disabled = false;
      btn.textContent = originalText;
    }
  }

  async exportReddit() {
    const components = Object.values(this.state.components);

    if (components.length === 0) {
      alert('No components selected. Please add components to your build first.');
      return;
    }

    const btn = this.container.querySelector('[data-action="export-reddit"]');
    const originalText = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Generating...';

    try {
      // Build Reddit-formatted table
      let redditText = '### PC Build\n\n';

      if (this.state.buildName) {
        redditText += `**Build Name:** ${this.state.buildName}\n\n`;
      }

      redditText += '| Type | Component | Price |\n';
      redditText += '|------|-----------|-------|\n';

      const typeOrder = ['cpu', 'cooler', 'motherboard', 'ram', 'storage', 'gpu', 'case', 'psu'];

      typeOrder.forEach(type => {
        const component = this.state.components[type];
        if (component) {
          const typeName = this.formatComponentType(type);
          const price = component.price ? `₱${parseFloat(component.price).toLocaleString('en-PH', {minimumFractionDigits: 2, maximumFractionDigits: 2})}` : 'N/A';
          redditText += `| ${typeName} | ${component.title} | ${price} |\n`;
        }
      });

      redditText += `| **Total** | | **₱${this.state.totalPrice.toLocaleString('en-PH', {minimumFractionDigits: 2, maximumFractionDigits: 2})}** |\n\n`;

      // Add power estimate
      if (this.state.totalPower > 0) {
        redditText += `**Estimated Wattage:** ${this.state.totalPower}W\n\n`;
      }

      // Add compatibility warnings
      if (this.state.warnings.length > 0) {
        redditText += '**Compatibility Notes:**\n\n';
        this.state.warnings.forEach(w => {
          redditText += `- [${w.priority.toUpperCase()}] ${w.message}\n`;
        });
        redditText += '\n';
      }

      redditText += '*Generated with PC Builder*';

      // Copy to clipboard
      await navigator.clipboard.writeText(redditText);
      alert('Reddit-formatted build copied to clipboard! You can now paste it in your post.');

    } catch (error) {
      console.error('Reddit export failed:', error);

      // Fallback: Show in modal for manual copy
      const modal = document.createElement('div');
      modal.style.cssText = 'position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);background:white;padding:2rem;border-radius:0.5rem;box-shadow:0 10px 25px rgba(0,0,0,0.3);z-index:9999;max-width:600px;max-height:80vh;overflow:auto;';

      const textarea = document.createElement('textarea');
      textarea.style.cssText = 'width:100%;min-height:300px;font-family:monospace;font-size:0.875rem;margin-bottom:1rem;';
      textarea.value = this.generateRedditText();
      textarea.select();

      const closeBtn = document.createElement('button');
      closeBtn.textContent = 'Close';
      closeBtn.className = 'btn btn--secondary';
      closeBtn.onclick = () => modal.remove();

      modal.appendChild(document.createTextNode('Copy this text:'));
      modal.appendChild(document.createElement('br'));
      modal.appendChild(document.createElement('br'));
      modal.appendChild(textarea);
      modal.appendChild(closeBtn);
      document.body.appendChild(modal);

      alert('Could not copy automatically. Please copy the text from the dialog.');
    } finally {
      btn.disabled = false;
      btn.textContent = originalText;
    }
  }

  generateRedditText() {
    const components = Object.values(this.state.components);
    let redditText = '### PC Build\n\n';

    if (this.state.buildName) {
      redditText += `**Build Name:** ${this.state.buildName}\n\n`;
    }

    redditText += '| Type | Component | Price |\n';
    redditText += '|------|-----------|-------|\n';

    const typeOrder = ['cpu', 'cooler', 'motherboard', 'ram', 'storage', 'gpu', 'case', 'psu'];

    typeOrder.forEach(type => {
      const component = this.state.components[type];
      if (component) {
        const typeName = this.formatComponentType(type);
        const price = component.price ? `₱${parseFloat(component.price).toLocaleString('en-PH', {minimumFractionDigits: 2, maximumFractionDigits: 2})}` : 'N/A';
        redditText += `| ${typeName} | ${component.title} | ${price} |\n`;
      }
    });

    redditText += `| **Total** | | **₱${this.state.totalPrice.toLocaleString('en-PH', {minimumFractionDigits: 2, maximumFractionDigits: 2})}** |\n\n`;

    if (this.state.totalPower > 0) {
      redditText += `**Estimated Wattage:** ${this.state.totalPower}W\n\n`;
    }

    if (this.state.warnings.length > 0) {
      redditText += '**Compatibility Notes:**\n\n';
      this.state.warnings.forEach(w => {
        redditText += `- [${w.priority.toUpperCase()}] ${w.message}\n`;
      });
      redditText += '\n';
    }

    redditText += '*Generated with PC Builder*';
    return redditText;
  }

  formatComponentType(type) {
    const labels = {
      cpu: 'CPU',
      motherboard: 'Motherboard',
      gpu: 'Graphics Card',
      ram: 'Memory (RAM)',
      storage: 'Storage',
      psu: 'Power Supply',
      case: 'Case',
      cooler: 'CPU Cooler'
    };
    return labels[type] || type;
  }

  checkComponentCompatibility(type, component) {
    const warnings = [];
    let compatible = true;
    const meta = component.metafields?.pcbuilder_v2 || {};

    // Check against currently selected components using pcbuilder_v2 namespace
    if (type === 'cpu' && this.state.components.motherboard) {
      const moboMeta = this.state.components.motherboard.metafields?.pcbuilder_v2;
      if (meta['cpu.socket'] && moboMeta?.['mobo.socket'] && meta['cpu.socket'] !== moboMeta['mobo.socket']) {
        warnings.push(`Socket mismatch: ${meta['cpu.socket']} vs ${moboMeta['mobo.socket']}`);
        compatible = false;
      }
    }

    if (type === 'motherboard' && this.state.components.cpu) {
      const cpuMeta = this.state.components.cpu.metafields?.pcbuilder_v2;
      if (meta['mobo.socket'] && cpuMeta?.['cpu.socket'] && meta['mobo.socket'] !== cpuMeta['cpu.socket']) {
        warnings.push(`Socket mismatch with CPU`);
        compatible = false;
      }
      if (meta['mobo.memory_type'] && this.state.components.ram) {
        const ramMeta = this.state.components.ram.metafields?.pcbuilder_v2;
        if (ramMeta?.['ram.memory_type'] && meta['mobo.memory_type'] !== ramMeta['ram.memory_type']) {
          warnings.push(`Memory type mismatch: ${meta['mobo.memory_type']} vs ${ramMeta['ram.memory_type']}`);
          compatible = false;
        }
      }
    }

    if (type === 'ram') {
      const moboMeta = this.state.components.motherboard?.metafields?.pcbuilder_v2;
      if (meta['ram.memory_type'] && moboMeta?.['mobo.memory_type']) {
        if (meta['ram.memory_type'] !== moboMeta['mobo.memory_type']) {
          warnings.push(`Incompatible with motherboard: ${meta['ram.memory_type']} vs ${moboMeta['mobo.memory_type']}`);
          compatible = false;
        }
      }
    }

    if (type === 'psu') {
      if (meta['psu.wattage'] && this.state.totalPower > 0) {
        const recommended = this.state.totalPower * 1.3;
        if (parseInt(meta['psu.wattage']) < recommended) {
          warnings.push(`May not provide enough power (${Math.round(recommended)}W recommended)`);
        }
      }
    }

    return { compatible, warnings };
  }

  updateCompatibilityHints(type) {
    const hints = [];
    if (type === 'cpu' || type === 'motherboard') {
      const cpu = this.state.components.cpu;
      const mobo = this.state.components.motherboard;
      if (cpu) hints.push(`Must be compatible with ${cpu.metafields?.pcbuilder_v2?.['cpu.socket'] || 'selected CPU'}`);
      if (mobo) hints.push(`Must be compatible with ${mobo.metafields?.pcbuilder_v2?.['mobo.socket'] || 'selected motherboard'}`);
    }
    // Could display hints in modal header
  }

  searchComponents(query) {
    console.log('Search triggered with query:', query);
    console.log('Total components:', this.allComponents.length);

    if (!query.trim()) {
      this.filteredComponents = this.allComponents;
    } else {
      const lowerQuery = query.toLowerCase();
      this.filteredComponents = this.allComponents.filter(c => {
        // Safely check title
        const title = c.title || '';
        const titleMatch = title.toLowerCase().includes(lowerQuery);

        // Safely check metafields
        const metaString = c.metafields ? JSON.stringify(c.metafields) : '';
        const metaMatch = metaString.toLowerCase().includes(lowerQuery);

        return titleMatch || metaMatch;
      });
    }

    console.log('Filtered components:', this.filteredComponents.length);

    // Apply stock filter if active
    this.applyStockFilter();
  }

  filterByStock(showOutOfStock) {
    console.log('Stock filter changed:', showOutOfStock ? 'Show all' : 'Hide out of stock');
    this.applyStockFilter();
  }

  applyStockFilter() {
    const stockFilter = this.modal.querySelector('[data-show-out-of-stock]');
    const showOutOfStock = stockFilter ? stockFilter.checked : true;

    let componentsToRender = this.filteredComponents;

    // If unchecked, filter out out-of-stock products
    if (!showOutOfStock) {
      componentsToRender = this.filteredComponents.filter(c => c.available === true);
      console.log(`Filtered to in-stock only: ${componentsToRender.length} products`);
    }

    this.renderComponents(componentsToRender);
  }

  async showRecommendations() {
    const components = Object.values(this.state.components);

    if (components.length === 0) {
      alert('Add some components first to get recommendations!');
      return;
    }

    // Show modal with strategy selection
    const modal = document.createElement('div');
    modal.className = 'modal is-open';
    modal.innerHTML = `
      <div class="modal__overlay"></div>
      <div class="modal__container">
        <div class="modal__header">
          <h3 class="modal__title">Get Recommendations</h3>
          <button type="button" class="modal__close" data-close-rec-modal>
            <svg class="icon" width="24" height="24"><use xlink:href="#icon-x"></use></svg>
          </button>
        </div>
        <div class="modal__body">
          <p style="margin-bottom: 1rem;">What kind of recommendations would you like?</p>
          <div style="display: flex; flex-direction: column; gap: 0.75rem;">
            <button type="button" class="btn btn--primary btn--full" data-rec-strategy="complete">
              Complete My Build
              <small style="display: block; font-size: 0.875rem; opacity: 0.9;">Get balanced recommendations for missing components</small>
            </button>
            <button type="button" class="btn btn--primary btn--full" data-rec-strategy="performance">
              Maximize Performance
              <small style="display: block; font-size: 0.875rem; opacity: 0.9;">Focus on high-end components</small>
            </button>
            <button type="button" class="btn btn--primary btn--full" data-rec-strategy="budget">
              Stay on Budget
              <small style="display: block; font-size: 0.875rem; opacity: 0.9;">Focus on value options</small>
            </button>
            <button type="button" class="btn btn--secondary btn--full" data-rec-strategy="upgrade">
              Upgrade Existing Components
              <small style="display: block; font-size: 0.875rem; opacity: 0.9;">Find better alternatives for what you have</small>
            </button>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    // Close handler
    modal.querySelector('[data-close-rec-modal]').addEventListener('click', () => modal.remove());
    modal.querySelector('.modal__overlay').addEventListener('click', () => modal.remove());

    // Strategy buttons
    modal.querySelectorAll('[data-rec-strategy]').forEach(btn => {
      btn.addEventListener('click', () => {
        const strategy = btn.dataset.recStrategy;
        modal.remove();
        this.getRecommendations(strategy);
      });
    });
  }

  async getRecommendations(strategy = 'complete') {
    try {
      // Prepare components for API
      const components = {};
      Object.entries(this.state.components).forEach(([type, component]) => {
        components[type] = {
          metafields: component.metafields
        };
      });

      const response = await fetch(`${this.apiUrl}/recommendations/get.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          components: components,
          strategy: strategy,
          limit: 3
        })
      });

      if (!response.ok) {
        throw new Error('Failed to get recommendations');
      }

      const result = await response.json();
      this.displayRecommendations(result.data.recommendations, strategy);

    } catch (error) {
      console.error('Get recommendations failed:', error);
      alert('Failed to get recommendations: ' + error.message);
    }
  }

  displayRecommendations(recommendations, strategy) {
    const recData = recommendations.recommendations || recommendations;

    if (Object.keys(recData).length === 0) {
      alert('No recommendations available. Your build may already be complete!');
      return;
    }

    // Create recommendations modal
    const modal = document.createElement('div');
    modal.className = 'modal is-open';

    let content = '';
    Object.entries(recData).forEach(([type, items]) => {
      if (Array.isArray(items) && items.length > 0) {
        const typeName = type.replace('_upgrades', '').toUpperCase();
        const isUpgrade = type.includes('_upgrades');

        content += `
          <div style="margin-bottom: 2rem;">
            <h4 style="font-size: 1.125rem; font-weight: 600; margin-bottom: 1rem;">
              ${isUpgrade ? '🔼 ' : '➕ '}${this.formatComponentType(typeName)}${isUpgrade ? ' Upgrades' : ''}
            </h4>
            <div style="display: flex; flex-direction: column; gap: 1rem;">
              ${items.map(item => this.renderRecommendationCard(item, typeName, isUpgrade)).join('')}
            </div>
          </div>
        `;
      }
    });

    modal.innerHTML = `
      <div class="modal__overlay"></div>
      <div class="modal__container" style="max-width: 800px;">
        <div class="modal__header">
          <h3 class="modal__title">Recommendations${strategy ? ': ' + strategy.charAt(0).toUpperCase() + strategy.slice(1) : ''}</h3>
          <button type="button" class="modal__close" data-close-rec-display>
            <svg class="icon" width="24" height="24"><use xlink:href="#icon-x"></use></svg>
          </button>
        </div>
        <div class="modal__body" style="max-height: 70vh; overflow-y: auto;">
          ${content}
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    // Close handlers
    modal.querySelector('[data-close-rec-display]').addEventListener('click', () => modal.remove());
    modal.querySelector('.modal__overlay').addEventListener('click', () => modal.remove());

    // Select recommendation handlers
    modal.querySelectorAll('[data-rec-select]').forEach(btn => {
      btn.addEventListener('click', () => {
        const productId = btn.dataset.recSelect;
        const type = btn.dataset.recType;
        const component = JSON.parse(btn.dataset.recComponent);
        this.selectComponent(type, component);
        modal.remove();
        alert(`Added ${component.title} to your build!`);
      });
    });
  }

  renderRecommendationCard(item, type, isUpgrade) {
    const image = item.image || item.product_data?.images?.[0]?.src || '';
    const price = item.price ? `₱${parseFloat(item.price).toLocaleString('en-PH', {minimumFractionDigits: 2, maximumFractionDigits: 2})}` : 'N/A';
    const score = item.recommendation_score ? Math.round(item.recommendation_score) : null;

    let badge = '';
    if (isUpgrade) {
      const increase = item.price_increase ? `+₱${parseFloat(item.price_increase).toLocaleString('en-PH', {minimumFractionDigits: 0, maximumFractionDigits: 0})}` : '';
      const gain = item.performance_gain ? `+${Math.round(item.performance_gain)}%` : '';
      badge = `<div style="font-size: 0.75rem; color: #059669; font-weight: 600;">${increase} ${gain}</div>`;
    } else if (score) {
      badge = `<div style="font-size: 0.75rem; color: #3b82f6; font-weight: 600;">Match: ${score}%</div>`;
    }

    return `
      <div style="display: flex; gap: 1rem; padding: 1rem; background: #f9fafb; border-radius: 0.5rem; border: 1px solid #e5e7eb;">
        <div style="width: 80px; height: 80px; flex-shrink: 0; background: white; border-radius: 0.375rem; overflow: hidden; display: flex; align-items: center; justify-content: center;">
          ${image ? `<img src="${image}" alt="${item.title}" style="width: 100%; height: 100%; object-fit: cover;">` :
            '<svg class="icon" width="32" height="32" style="color: #d1d5db;"><use xlink:href="#icon-image"></use></svg>'}
        </div>
        <div style="flex: 1; display: flex; flex-direction: column; gap: 0.5rem;">
          <div>
            <div style="font-weight: 600; line-height: 1.3;">${item.title}</div>
            <div style="display: flex; gap: 1rem; align-items: center; margin-top: 0.25rem;">
              <div style="font-size: 1.125rem; font-weight: 700; color: #059669;">${price}</div>
              ${badge}
            </div>
          </div>
          ${item.warnings && item.warnings.length > 0 ? `
            <div style="font-size: 0.75rem; color: #d97706;">
              ⚠️ ${item.warnings[0].message}
            </div>
          ` : ''}
        </div>
        <div style="display: flex; align-items: center;">
          <button type="button" class="btn btn--small btn--primary"
                  data-rec-select="${item.id}"
                  data-rec-type="${type.toLowerCase()}"
                  data-rec-component='${JSON.stringify(item)}'>
            ${isUpgrade ? 'Upgrade' : 'Select'}
          </button>
        </div>
      </div>
    `;
  }
}

// Initialize on DOMContentLoaded
document.addEventListener('DOMContentLoaded', () => {
  const container = document.querySelector('.pc-builder');
  if (container) {
    new PCBuilder(container);
  }
});
